import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { vehiclePrimaryImage, vehicleGallery } from '@/utils/vehicleImages';
import { formatVehiclePrice } from '@/utils/vehiclePrice';
import { formatDateMdY } from '@/utils/dateFormat';
import { US_STATES } from '@/constants/usStates';
import VehicleStatusChangeModal from '@/components/admin/VehicleStatusChangeModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function DetailRow({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 py-2 border-b border-white/5">
      <div className="text-xs font-['Manrope'] text-white/45 uppercase tracking-wide">{label}</div>
      <div className="sm:col-span-2 text-sm font-['Manrope'] text-white">{value}</div>
    </div>
  );
}

export default function SoldVehicleDetailPage() {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusOpen, setStatusOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creatingDeal, setCreatingDeal] = useState(false);
  const [lienHolders, setLienHolders] = useState([]);
  const [form, setForm] = useState({});

  const fetchVehicle = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/vehicles/${vehicleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVehicle(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load sold vehicle');
      navigate('/admin/sold-vehicles');
    } finally {
      setLoading(false);
    }
  }, [token, vehicleId, navigate]);

  useEffect(() => {
    if (isAuthenticated && token && vehicleId) fetchVehicle();
  }, [isAuthenticated, token, vehicleId, fetchVehicle]);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/admin/lien-holders`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setLienHolders(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setLienHolders([]);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return <div className="p-6 text-white/60 font-['Manrope']">Loading...</div>;
  }
  if (!vehicle) return null;

  const sale = vehicle.sale || {};
  const gallery = vehicleGallery(vehicle);
  const address = [sale.street_address, [sale.city, sale.state].filter(Boolean).join(', '), sale.zip]
    .filter(Boolean)
    .join(', ');
  const matchingDealId = vehicle.matching_deal_id || sale.deal_id || '';
  const canAddToDeals = (sale.source === 'lead' || sale.source === 'manual') && !matchingDealId;
  const canViewDeal = Boolean(matchingDealId);

  const startEdit = () => {
    setForm({
      purchase_type: sale.purchase_type || 'cash',
      sale_price: sale.sale_price != null ? String(sale.sale_price) : '',
      customer_name: sale.customer_name || '',
      street_address: sale.street_address || '',
      city: sale.city || '',
      state: sale.state || '',
      zip: sale.zip || '',
      dl_number: sale.dl_number || '',
      dl_issuing_county: sale.dl_issuing_county || '',
      dl_issuing_state: sale.dl_issuing_state || '',
      dl_expiry: sale.dl_expiry || '',
      date_of_birth: sale.date_of_birth || '',
      phone: sale.phone || '',
      email: sale.email || '',
      lien_holder_id: sale.lien_holder_id || sale.lien_holder?.id || '',
    });
    setEditing(true);
  };

  const saveCustomer = async (e) => {
    e.preventDefault();
    if (!form.customer_name?.trim()) {
      toast.error('Customer full name is required');
      return;
    }
    const price = Number(form.sale_price);
    if (form.sale_price === '' || !Number.isFinite(price) || price < 0) {
      toast.error('Sale price is required');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${BACKEND_URL}/api/admin/vehicles/${vehicle.id}/sale`,
        {
          ...form,
          sale_price: price,
          lien_holder_id: form.purchase_type === 'financing' ? (form.lien_holder_id || null) : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVehicle(data);
      setEditing(false);
      toast.success('Customer information updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update customer info');
    } finally {
      setSaving(false);
    }
  };

  const addToDealsHub = async () => {
    setCreatingDeal(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/vehicles/${vehicle.id}/add-to-deals-hub`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.vehicle) setVehicle(data.vehicle);
      toast.success(data.already_existed
        ? 'This customer already has a deal for this vehicle'
        : 'Deal created in Deals Hub');
      if (data.deal_id) navigate(`/admin/deals/${data.deal_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create deal');
    } finally {
      setCreatingDeal(false);
    }
  };

  const editCls =
    "w-full bg-white/5 border border-white/10 text-white px-3 py-2 font-['Manrope'] text-sm focus:outline-none focus:border-blue-500/50";
  const editLabel = "block text-xs font-['Manrope'] text-white/50 mb-1";

  return (
    <div className="p-4 sm:p-6 max-w-[1100px] mx-auto" data-testid="sold-vehicle-detail">
      <button
        type="button"
        onClick={() => navigate('/admin/sold-vehicles')}
        className="inline-flex items-center gap-2 text-sm font-['Manrope'] text-white/50 hover:text-white mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Sold Vehicles
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-['Unbounded'] font-bold text-white">
            {vehicle.year} {vehicle.make} {vehicle.model}
          </h1>
          <p className="text-sm font-['Manrope'] text-white/40 mt-1">{vehicle.vin}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canViewDeal && (
            <button
              type="button"
              onClick={() => navigate(`/admin/deals/${matchingDealId}`)}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white font-['Manrope'] font-semibold"
            >
              View Deal
            </button>
          )}
          {canAddToDeals && (
            <button
              type="button"
              disabled={creatingDeal}
              onClick={addToDealsHub}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-['Manrope'] font-semibold disabled:opacity-50"
            >
              {creatingDeal ? 'Creating…' : 'Add to Deals Hub'}
            </button>
          )}
          <button
            type="button"
            onClick={() => setStatusOpen(true)}
            className="px-4 py-2 border border-white/15 text-white font-['Manrope'] hover:bg-white/5"
          >
            Return to Inventory
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="bg-white/5 border border-white/10 p-6">
          <div className="flex items-start justify-between gap-3 mb-4">
            <h2 className="text-lg font-['Unbounded'] font-semibold text-white">Customer Information</h2>
            {!editing && (
              <button
                type="button"
                onClick={startEdit}
                className="p-2 hover:bg-white/10 text-blue-400"
                title="Edit customer information"
              >
                <Pencil className="w-4 h-4" />
              </button>
            )}
          </div>
          {editing ? (
            <form onSubmit={saveCustomer} className="space-y-3">
              <div>
                <label className={editLabel}>Purchase Type *</label>
                <select value={form.purchase_type} onChange={(e) => setForm({ ...form, purchase_type: e.target.value })} className={`${editCls} [&>option]:bg-gray-900`}>
                  <option value="cash">Cash</option>
                  <option value="financing">Financing</option>
                </select>
              </div>
              <div>
                <label className={editLabel}>Sale Price *</label>
                <input type="number" min="0" required value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} className={editCls} />
              </div>
              <div>
                <label className={editLabel}>Full Name *</label>
                <input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className={editCls} />
              </div>
              <div>
                <label className={editLabel}>Street Address</label>
                <input value={form.street_address} onChange={(e) => setForm({ ...form, street_address: e.target.value })} className={editCls} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={editCls} />
                <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={`${editCls} [&>option]:bg-gray-900`}>
                  <option value="">ST</option>
                  {US_STATES.map((st) => <option key={st} value={st}>{st}</option>)}
                </select>
                <input placeholder="ZIP" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className={editCls} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="DL number" value={form.dl_number} onChange={(e) => setForm({ ...form, dl_number: e.target.value })} className={editCls} />
                <input placeholder="DL county" value={form.dl_issuing_county} onChange={(e) => setForm({ ...form, dl_issuing_county: e.target.value })} className={editCls} />
                <select value={form.dl_issuing_state} onChange={(e) => setForm({ ...form, dl_issuing_state: e.target.value })} className={`${editCls} [&>option]:bg-gray-900`}>
                  <option value="">DL state</option>
                  {US_STATES.map((st) => <option key={st} value={st}>{st}</option>)}
                </select>
                <input type="date" value={form.dl_expiry} onChange={(e) => setForm({ ...form, dl_expiry: e.target.value })} className={editCls} />
              </div>
              <div>
                <label className={editLabel}>Date of Birth</label>
                <input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className={editCls} />
              </div>
              {form.purchase_type === 'financing' && (
                <div>
                  <label className={editLabel}>Lien Holder</label>
                  <select value={form.lien_holder_id} onChange={(e) => setForm({ ...form, lien_holder_id: e.target.value })} className={`${editCls} [&>option]:bg-gray-900`}>
                    <option value="">Select lien holder…</option>
                    {lienHolders.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={editCls} />
                <input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={editCls} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEditing(false)} className="px-3 py-2 text-sm text-white/60 font-['Manrope']">Cancel</button>
                <button type="submit" disabled={saving} className="min-h-11 w-full sm:w-auto px-4 py-2 bg-blue-500 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          ) : (
            <>
              <DetailRow label="Name" value={sale.customer_name} />
              <DetailRow label="Purchase Type" value={sale.purchase_type ? sale.purchase_type.charAt(0).toUpperCase() + sale.purchase_type.slice(1) : ''} />
              <DetailRow label="Sale Price" value={sale.sale_price != null ? `$${Number(sale.sale_price).toLocaleString()}` : ''} />
              <DetailRow label="Phone" value={sale.phone} />
              <DetailRow label="Email" value={sale.email} />
              <DetailRow label="Address" value={address} />
              <DetailRow label="Date of Birth" value={sale.date_of_birth ? formatDateMdY(sale.date_of_birth) : ''} />
              <DetailRow label="DL Number" value={sale.dl_number} />
              <DetailRow label="DL Issuing County" value={sale.dl_issuing_county} />
              <DetailRow label="DL Issuing State" value={sale.dl_issuing_state} />
              <DetailRow label="DL Expiring Date" value={sale.dl_expiry ? formatDateMdY(sale.dl_expiry) : ''} />
              {sale.purchase_type === 'financing' && (
                <DetailRow label="Lien Holder" value={sale.lien_holder?.name || ''} />
              )}
            </>
          )}
        </section>

        <section className="bg-white/5 border border-white/10 p-6">
          <h2 className="text-lg font-['Unbounded'] font-semibold text-white mb-4">Vehicle Information</h2>
          <img
            src={vehiclePrimaryImage(vehicle)}
            alt=""
            className="w-full aspect-[16/9] object-cover mb-4 border border-white/10"
          />
          <DetailRow label="Year" value={vehicle.year} />
          <DetailRow label="Make" value={vehicle.make} />
          <DetailRow label="Model" value={vehicle.model} />
          <DetailRow label="Trim" value={vehicle.trim} />
          <DetailRow label="VIN" value={vehicle.vin} />
          <DetailRow label="Stock #" value={vehicle.stockNumber} />
          <DetailRow label="List Price" value={formatVehiclePrice(vehicle)} />
          <DetailRow label="Sale Price" value={sale.sale_price != null ? `$${Number(sale.sale_price).toLocaleString()}` : ''} />
          <DetailRow label="Mileage" value={vehicle.mileage != null ? `${Number(vehicle.mileage).toLocaleString()} mi` : ''} />
          <DetailRow label="Body Type" value={vehicle.body_type} />
          <DetailRow label="Exterior" value={vehicle.exterior_color} />
          <DetailRow label="Interior" value={vehicle.interior_color} />
          <DetailRow label="Engine" value={vehicle.engine} />
          <DetailRow label="Fuel" value={vehicle.fuel_type} />
          <DetailRow label="Transmission" value={vehicle.transmission} />
          <DetailRow label="Drivetrain" value={vehicle.drivetrain} />
          <DetailRow label="Sold On" value={vehicle.sold_at ? formatDateMdY(vehicle.sold_at) : ''} />
          {gallery.length > 1 && (
            <div className="grid grid-cols-4 gap-2 mt-4">
              {gallery.slice(0, 8).map((url) => (
                <img key={url} src={url} alt="" className="aspect-[4/3] object-cover" />
              ))}
            </div>
          )}
        </section>
      </div>

      <VehicleStatusChangeModal
        isOpen={statusOpen}
        vehicle={vehicle}
        token={token}
        mode="sold"
        onClose={() => setStatusOpen(false)}
        onSuccess={() => {
          setStatusOpen(false);
          toast.success('Vehicle returned to inventory');
          navigate('/admin/inventory');
        }}
      />
    </div>
  );
}
